import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { coursesAPI } from '../services/api';
import { getCourseThumbnail } from '../utils/courseMedia';

const InstructorDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('create'); // 'create' or 'manage'
    const [courses, setCourses] = useState([]);
    const [currentStep, setCurrentStep] = useState(1); // Multi-step form: 1, 2, 3
    const [editingCourseId, setEditingCourseId] = useState(null); // Track editing state

    // Comprehensive Course Creation Form State
    const [courseData, setCourseData] = useState({
        // Basic Info
        title: '',
        description: '',
        level: 'Intermediate',
        category: 'Communication Skills',
        image: '',
        price: 0,
        duration: '', // e.g., "8 weeks"
        language: 'English',
        
        // Additional Details
        prerequisites: '',
        learningObjectives: [''],
        tags: [],
        
        // Course Content
        modules: [
            {
                id: 1,
                title: '',
                description: '',
                lessons: [
                    {
                        id: 1,
                        title: '',
                        description: '',
                        duration: '',
                        videoUrl: '',
                        resources: []
                    }
                ]
            }
        ]
    });

    const [tagInput, setTagInput] = useState('');

    const categories = [
        'Communication Skills',
        'Grammar',
        'Writing',
        'Vocabulary',
        'Business English',
        'IELTS Prep',
        'TOEFL Prep',
        'Pronunciation',
        'Listening',
        'Reading',
        'Conversation Skills',
        'Interview Preparation'
    ];

    // Load courses on mount
    useEffect(() => {
        if (activeTab === 'manage') {
            fetchCourses();
        }
    }, [activeTab]);

    const fetchCourses = async () => {
        try {
            // Only fetch instructor's own courses, not all courses
            const response = await coursesAPI.getMyCourses();
            setCourses(response.data || []);
        } catch (error) {
            console.error('Failed to fetch courses:', error);
        }
    };

    // Helper Functions
    const addLearningObjective = () => {
        setCourseData({
            ...courseData,
            learningObjectives: [...courseData.learningObjectives, '']
        });
    };

    const updateLearningObjective = (index, value) => {
        const updated = [...courseData.learningObjectives];
        updated[index] = value;
        setCourseData({ ...courseData, learningObjectives: updated });
    };

    const removeLearningObjective = (index) => {
        const updated = courseData.learningObjectives.filter((_, i) => i !== index);
        setCourseData({ ...courseData, learningObjectives: updated });
    };

    const addTag = () => {
        if (tagInput.trim() && !courseData.tags.includes(tagInput.trim())) {
            setCourseData({
                ...courseData,
                tags: [...courseData.tags, tagInput.trim()]
            });
            setTagInput('');
        }
    };

    const removeTag = (tag) => {
        setCourseData({
            ...courseData,
            tags: courseData.tags.filter(t => t !== tag)
        });
    };

    const addModule = () => {
        setCourseData({
            ...courseData,
            modules: [
                ...courseData.modules,
                {
                    id: courseData.modules.length + 1,
                    title: '',
                    description: '',
                    lessons: [
                        {
                            id: 1,
                            title: '',
                            description: '',
                            duration: '',
                            videoUrl: '',
                            resources: []
                        }
                    ]
                }
            ]
        });
    };

    const updateModule = (moduleIndex, field, value) => {
        const updated = [...courseData.modules];
        updated[moduleIndex] = { ...updated[moduleIndex], [field]: value };
        setCourseData({ ...courseData, modules: updated });
    };

    const removeModule = (moduleIndex) => {
        const updated = courseData.modules.filter((_, i) => i !== moduleIndex);
        setCourseData({ ...courseData, modules: updated });
    };

    const addLesson = (moduleIndex) => {
        const updated = [...courseData.modules];
        updated[moduleIndex].lessons.push({
            id: updated[moduleIndex].lessons.length + 1,
            title: '',
            description: '',
            duration: '',
            videoUrl: '',
            resources: []
        });
        setCourseData({ ...courseData, modules: updated });
    };

    const updateLesson = (moduleIndex, lessonIndex, field, value) => {
        const updated = [...courseData.modules];
        updated[moduleIndex].lessons[lessonIndex] = {
            ...updated[moduleIndex].lessons[lessonIndex],
            [field]: value
        };
        setCourseData({ ...courseData, modules: updated });
    };

    const removeLesson = (moduleIndex, lessonIndex) => {
        const updated = [...courseData.modules];
        updated[moduleIndex].lessons = updated[moduleIndex].lessons.filter((_, i) => i !== lessonIndex);
        setCourseData({ ...courseData, modules: updated });
    };

    const resetForm = () => {
        setCourseData({
            title: '',
            description: '',
            level: 'Intermediate',
            category: 'Communication Skills',
            image: '',
            price: 0,
            duration: '', 
            language: 'English',
            prerequisites: '',
            learningObjectives: [''],
            tags: [],
            modules: [
                {
                    id: 1,
                    title: '',
                    description: '',
                    lessons: [
                        {
                            id: 1,
                            title: '',
                            description: '',
                            duration: '',
                            videoUrl: '',
                            resources: []
                        }
                    ]
                }
            ]
        });
        setTagInput('');
        setEditingCourseId(null);
        setCurrentStep(1);
    };

    const handleEditCourse = async (courseId) => {
        setLoading(true);
        try {
            const response = await coursesAPI.getById(courseId);
            const course = response.data;
            
            // Parse full_json if it exists, otherwise reconstruct basic structure
            const fullData = course.full_json || {};
            
            const transformedData = {
                title: course.title,
                description: course.description || '',
                level: course.level || 'Intermediate',
                category: course.category || 'Communication Skills',
                image: course.image || '',
                price: course.price || 0,
                duration: '', // Not in basic model, might be in full_json
                language: 'English',
                
                // Try to extract from full_json, fallback to defaults
                prerequisites: fullData.prerequisites || '',
                learningObjectives: fullData.learningObjectives || [''],
                tags: fullData.tags || [],
                modules: fullData.modules || [
                    {
                        id: 1,
                        title: '',
                        description: '',
                        lessons: [{ id: 1, title: '', description: '', duration: '', videoUrl: '', resources: [] }]
                    }
                ]
            };
            
            setCourseData(transformedData);
            setEditingCourseId(courseId);
            setActiveTab('create');
            window.scrollTo(0, 0);
        } catch (error) {
            console.error('Failed to fetch course details:', error);
            alert('Failed to load course details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCourse = async (courseId) => {
        if (window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
            setLoading(true);
            try {
                await coursesAPI.delete(courseId);
                // Refresh list
                fetchCourses();
            } catch (error) {
                console.error('Failed to delete course:', error);
                alert('Failed to delete course. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCreateCourse = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Transform data to match Backend Pydantic Model
            const payload = {
                title: courseData.title,
                description: courseData.description,
                level: courseData.level,
                category: courseData.category,
                price: courseData.price,
                image: courseData.image || '',
                full_json: {
                    modules: courseData.modules,
                    curriculum: courseData.modules.map((module, idx) => ({
                        moduleNumber: idx + 1,
                        moduleTitle: module.title,
                        lessons: module.lessons.map((lesson, lidx) => ({
                            lessonNumber: lidx + 1,
                            lessonTitle: lesson.title,
                            duration: lesson.duration
                        }))
                    })),
                    duration: courseData.duration,
                    prerequisites: courseData.prerequisites,
                    learningObjectives: courseData.learningObjectives.filter(obj => obj.trim() !== ''),
                    tags: courseData.tags,
                    language: courseData.language
                }
            };

            if (editingCourseId) {
                try {
                    await coursesAPI.update(editingCourseId, payload);
                    alert('✅ Course updated successfully!');
                } catch (updateError) {
                    const statusCode = updateError.response?.status;
                    if (statusCode === 403 || statusCode === 404) {
                        // Course ownership/id may have changed in production; recover by creating a new course.
                        await coursesAPI.create(payload);
                        alert('✅ Existing course could not be updated, so a new course was created successfully.');
                    } else {
                        throw updateError;
                    }
                }
            } else {
                await coursesAPI.create(payload);
                alert('✅ Course created successfully!');
            }
            
            // Reset form
            resetForm();
            setActiveTab('manage');
        } catch (error) {
            console.error(error);
            const serverDetail = error.response?.data?.detail;
            const networkHint = error.message === 'Network Error'
                ? 'Network Error. The API server may be unreachable from this domain. Please hard refresh and try again.'
                : error.message;

            const authFailed =
                error.response?.status === 401 ||
                (typeof serverDetail === 'string' && serverDetail.toLowerCase().includes('validate credentials'));

            if (authFailed) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                alert('❌ Session expired. Please log in again to create or update courses.');
                navigate('/login');
                return;
            }

            alert(`❌ Failed to ${editingCourseId ? 'update' : 'create'} course: ` + (serverDetail || networkHint));
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    const goToNextStep = () => {
        if (currentStep < 3) setCurrentStep(currentStep + 1);
    };

    const goToPreviousStep = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };


    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerContent}>
                    <div>
                        <h1 style={styles.headerTitle}>📚 Instructor Dashboard</h1>
                        <p style={styles.headerSubtitle}>Create and manage your courses</p>
                    </div>
                    <div style={styles.headerActions}>
                        <button type="button" onClick={() => { window.location.href = '/'; }} style={styles.homeButton}>
                            🏠 Home
                        </button>
                        <button type="button" onClick={handleLogout} style={styles.logoutButton}>
                            🚪 Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div style={styles.main}>
                {/* Tab Navigation */}
                <div style={styles.tabContainer}>
                    <button
                        onClick={() => setActiveTab('create')}
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'create' ? styles.tabActive : {})
                        }}
                    >
                        ✨ Create Course
                    </button>
                    <button
                        onClick={() => setActiveTab('manage')}
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'manage' ? styles.tabActive : {})
                        }}
                    >
                        📋 Manage Courses
                    </button>
                </div>

                {/* Create Course Tab */}
                {activeTab === 'create' && (
                    <div style={styles.content}>
                        {/* Progress Steps */}
                        <div style={styles.progressContainer}>
                            <div style={styles.stepItem}>
                                <div style={currentStep >= 1 ? styles.stepActive : styles.stepInactive}>1</div>
                                <span style={styles.stepLabel}>Basic Info</span>
                            </div>
                            <div style={styles.stepLine}></div>
                            <div style={styles.stepItem}>
                                <div style={currentStep >= 2 ? styles.stepActive : styles.stepInactive}>2</div>
                                <span style={styles.stepLabel}>Course Details</span>
                            </div>
                            <div style={styles.stepLine}></div>
                            <div style={styles.stepItem}>
                                <div style={currentStep >= 3 ? styles.stepActive : styles.stepInactive}>3</div>
                                <span style={styles.stepLabel}>Modules & Lessons</span>
                            </div>
                        </div>

                        {editingCourseId && (
                            <div style={{
                                backgroundColor: '#fff7ed', 
                                border: '1px solid #fdba74', 
                                padding: '15px', 
                                borderRadius: '8px',
                                marginBottom: '20px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div>
                                    <strong style={{color: '#9a3412'}}>✏️ Editing Mode</strong>
                                    <div style={{fontSize: '0.9rem', color: '#c2410c'}}>You are updating an existing course.</div>
                                </div>
                                <button 
                                    type="button" 
                                    onClick={resetForm}
                                    style={{
                                        backgroundColor: 'white',
                                        border: '1px solid #fdba74',
                                        color: '#c2410c',
                                        padding: '5px 15px',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel Editing
                                </button>
                            </div>
                        )}

                        <form onSubmit={handleCreateCourse} style={styles.form}>
                            {/* Step 1: Basic Information */}
                            {currentStep === 1 && (
                                <div style={styles.stepContent}>
                                    <h2 style={styles.stepTitle}>📝 Basic Course Information</h2>

                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Course Title *</label>
                                        <input
                                            type="text"
                                            style={styles.input}
                                            value={courseData.title}
                                            onChange={(e) => setCourseData({ ...courseData, title: e.target.value })}
                                            placeholder="e.g., Master English Speaking in 30 Days"
                                            required
                                        />
                                    </div>

                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Course Description *</label>
                                        <textarea
                                            style={styles.textarea}
                                            value={courseData.description}
                                            onChange={(e) => setCourseData({ ...courseData, description: e.target.value })}
                                            placeholder="Describe what students will learn, who it's for, and what makes it unique..."
                                            required
                                            rows="6"
                                        />
                                        <small style={styles.hint}>
                                            {courseData.description.length} characters
                                        </small>
                                    </div>

                                    <div style={styles.gridTwo}>
                                        <div style={styles.formGroup}>
                                            <label style={styles.label}>Category *</label>
                                            <select
                                                style={styles.select}
                                                value={courseData.category}
                                                onChange={(e) => setCourseData({ ...courseData, category: e.target.value })}
                                            >
                                                {categories.map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div style={styles.formGroup}>
                                            <label style={styles.label}>Level *</label>
                                            <select
                                                style={styles.select}
                                                value={courseData.level}
                                                onChange={(e) => setCourseData({ ...courseData, level: e.target.value })}
                                            >
                                                <option value="Beginner">Beginner</option>
                                                <option value="Intermediate">Intermediate</option>
                                                <option value="Advanced">Advanced</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div style={styles.gridTwo}>
                                        <div style={styles.formGroup}>
                                            <label style={styles.label}>Price (₹) *</label>
                                            <input
                                                type="number"
                                                style={styles.input}
                                                value={courseData.price}
                                                onChange={(e) => setCourseData({ ...courseData, price: parseInt(e.target.value) || 0 })}
                                                placeholder="0 for Free"
                                                min="0"
                                            />
                                            <small style={styles.hint}>Set to 0 for free course</small>
                                        </div>

                                        <div style={styles.formGroup}>
                                            <label style={styles.label}>Duration</label>
                                            <input
                                                type="text"
                                                style={styles.input}
                                                value={courseData.duration}
                                                onChange={(e) => setCourseData({ ...courseData, duration: e.target.value })}
                                                placeholder="e.g., 8 weeks, 30 days"
                                            />
                                        </div>
                                    </div>

                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Course Image URL (Optional)</label>
                                        <input
                                            type="url"
                                            style={styles.input}
                                            value={courseData.image}
                                            onChange={(e) => setCourseData({ ...courseData, image: e.target.value })}
                                            placeholder="https://example.com/course-image.jpg"
                                        />
                                        <small style={styles.hint}>
                                            Leave blank for auto-generated image from Unsplash
                                        </small>
                                        {courseData.image && (
                                            <img
                                                src={courseData.image}
                                                alt="Course preview"
                                                style={styles.imagePreview}
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        )}
                                    </div>

                                    <div style={styles.buttonGroup}>
                                        <button
                                            type="button"
                                            onClick={goToNextStep}
                                            style={styles.nextButton}
                                        >
                                            Next Step →
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Course Details */}
                            {currentStep === 2 && (
                                <div style={styles.stepContent}>
                                    <h2 style={styles.stepTitle}>🎯 Course Details</h2>

                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Prerequisites</label>
                                        <textarea
                                            style={styles.textarea}
                                            value={courseData.prerequisites}
                                            onChange={(e) => setCourseData({ ...courseData, prerequisites: e.target.value })}
                                            placeholder="What should students know before taking this course? (Optional)"
                                            rows="3"
                                        />
                                    </div>

                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Learning Objectives *</label>
                                        <small style={styles.hint}>What will students be able to do after completing this course?</small>
                                        {courseData.learningObjectives.map((objective, index) => (
                                            <div key={index} style={styles.objectiveItem}>
                                                <input
                                                    type="text"
                                                    style={styles.inputWithButton}
                                                    value={objective}
                                                    onChange={(e) => updateLearningObjective(index, e.target.value)}
                                                    placeholder={`Objective ${index + 1}`}
                                                />
                                                {courseData.learningObjectives.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLearningObjective(index)}
                                                        style={styles.removeButton}
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={addLearningObjective}
                                            style={styles.addButton}
                                        >
                                            + Add Objective
                                        </button>
                                    </div>

                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Tags</label>
                                        <div style={styles.tagsContainer}>
                                            {courseData.tags.map((tag, index) => (
                                                <span key={index} style={styles.tag}>
                                                    {tag}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTag(tag)}
                                                        style={styles.tagClose}
                                                    >
                                                        ✕
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <div style={styles.tagInputGroup}>
                                            <input
                                                type="text"
                                                style={styles.input}
                                                value={tagInput}
                                                onChange={(e) => setTagInput(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addTag();
                                                    }
                                                }}
                                                placeholder="Enter tag and press Enter or click Add"
                                            />
                                            <button
                                                type="button"
                                                onClick={addTag}
                                                style={styles.tagAddButton}
                                            >
                                                Add Tag
                                            </button>
                                        </div>
                                    </div>

                                    <div style={styles.buttonGroup}>
                                        <button
                                            type="button"
                                            onClick={goToPreviousStep}
                                            style={styles.backButton}
                                        >
                                            ← Back
                                        </button>
                                        <button
                                            type="button"
                                            onClick={goToNextStep}
                                            style={styles.nextButton}
                                        >
                                            Next Step →
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Modules & Lessons */}
                            {currentStep === 3 && (
                                <div style={styles.stepContent}>
                                    <h2 style={styles.stepTitle}>📚 Modules & Lessons</h2>

                                    {courseData.modules.map((module, moduleIndex) => (
                                        <div key={module.id} style={styles.moduleCard}>
                                            <div style={styles.moduleHeader}>
                                                <h3 style={styles.moduleTitle}>Module {moduleIndex + 1}</h3>
                                                {courseData.modules.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeModule(moduleIndex)}
                                                        style={styles.deleteButton}
                                                    >
                                                        🗑️ Delete Module
                                                    </button>
                                                )}
                                            </div>

                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Module Title *</label>
                                                <input
                                                    type="text"
                                                    style={styles.input}
                                                    value={module.title}
                                                    onChange={(e) => updateModule(moduleIndex, 'title', e.target.value)}
                                                    placeholder="e.g., Introduction to English Grammar"
                                                    required
                                                />
                                            </div>

                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Module Description</label>
                                                <textarea
                                                    style={styles.textareaSmall}
                                                    value={module.description}
                                                    onChange={(e) => updateModule(moduleIndex, 'description', e.target.value)}
                                                    placeholder="Brief description of this module"
                                                    rows="2"
                                                />
                                            </div>

                                            {/* Lessons */}
                                            <div style={styles.lessonsContainer}>
                                                <h4 style={styles.lessonsTitle}>📖 Lessons</h4>
                                                {module.lessons.map((lesson, lessonIndex) => (
                                                    <div key={lesson.id} style={styles.lessonCard}>
                                                        <div style={styles.lessonHeader}>
                                                            <span style={styles.lessonNumber}>Lesson {lessonIndex + 1}</span>
                                                            {module.lessons.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeLesson(moduleIndex, lessonIndex)}
                                                                    style={styles.removeButtonSmall}
                                                                >
                                                                    ✕ Remove
                                                                </button>
                                                            )}
                                                        </div>

                                                        <div style={styles.formGroup}>
                                                            <input
                                                                type="text"
                                                                style={styles.input}
                                                                value={lesson.title}
                                                                onChange={(e) => updateLesson(moduleIndex, lessonIndex, 'title', e.target.value)}
                                                                placeholder="Lesson title"
                                                                required
                                                            />
                                                        </div>

                                                        <div style={styles.formGroup}>
                                                            <textarea
                                                                style={styles.textareaSmall}
                                                                value={lesson.description}
                                                                onChange={(e) => updateLesson(moduleIndex, lessonIndex, 'description', e.target.value)}
                                                                placeholder="Lesson description"
                                                                rows="2"
                                                            />
                                                        </div>

                                                        <div style={styles.gridTwo}>
                                                            <div style={styles.formGroup}>
                                                                <input
                                                                    type="text"
                                                                    style={styles.input}
                                                                    value={lesson.duration}
                                                                    onChange={(e) => updateLesson(moduleIndex, lessonIndex, 'duration', e.target.value)}
                                                                    placeholder="Duration (e.g., 15 min)"
                                                                />
                                                            </div>
                                                            <div style={styles.formGroup}>
                                                                <input
                                                                    type="url"
                                                                    style={styles.input}
                                                                    value={lesson.videoUrl}
                                                                    onChange={(e) => updateLesson(moduleIndex, lessonIndex, 'videoUrl', e.target.value)}
                                                                    placeholder="Video URL (optional)"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                <button
                                                    type="button"
                                                    onClick={() => addLesson(moduleIndex)}
                                                    style={styles.addLessonButton}
                                                >
                                                    + Add Lesson
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={addModule}
                                        style={styles.addModuleButton}
                                    >
                                        + Add Module
                                    </button>

                                    <div style={styles.buttonGroup}>
                                        <button
                                            type="button"
                                            onClick={goToPreviousStep}
                                            style={styles.backButton}
                                        >
                                            ← Back
                                        </button>
                                        <button
                                            type="submit"
                                            style={{
                                                ...styles.publishButton,
                                                opacity: loading ? 0.7 : 1,
                                                cursor: loading ? 'not-allowed' : 'pointer'
                                            }}
                                            disabled={loading}
                                        >
                                            {loading ? '⏳ Publishing...' : '🚀 Publish Course'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                )}

                {/* Manage Courses Tab */}
                {activeTab === 'manage' && (
                    <div style={styles.content}>
                        <div style={styles.manageHeader}>
                            <h2 style={styles.manageTitle}>Your Courses</h2>
                            <p style={styles.manageSubtitle}>
                                {courses.length} course{courses.length !== 1 ? 's' : ''} published
                            </p>
                        </div>

                        {courses.length === 0 ? (
                            <div style={styles.emptyState}>
                                <div style={styles.emptyIcon}>📚</div>
                                <h3 style={styles.emptyTitle}>No courses yet</h3>
                                <p style={styles.emptyText}>
                                    Create your first course to get started!
                                </p>
                                <button
                                    onClick={() => setActiveTab('create')}
                                    style={styles.createFirstButton}
                                >
                                    Create Your First Course
                                </button>
                            </div>
                        ) : (
                            <div style={styles.coursesGrid}>
                                {courses.map((course) => (
                                    <div key={course.id} style={styles.courseCard}>
                                        {getCourseThumbnail(course) && (
                                            <img
                                                src={getCourseThumbnail(course)}
                                                alt={course.title}
                                                style={styles.courseImage}
                                            />
                                        )}
                                        <div style={styles.courseContent}>
                                            <div style={styles.courseBadge}>
                                                {course.category}
                                            </div>
                                            <h3 style={styles.courseTitle}>{course.title}</h3>
                                            <p style={styles.courseDescription}>
                                                {course.description?.substring(0, 100)}...
                                            </p>
                                            <div style={styles.courseFooter}>
                                                <span style={styles.coursePrice}>
                                                    {course.price === 0 ? 'Free' : `₹${course.price}`}
                                                </span>
                                                <span style={styles.courseLevel}>{course.level}</span>
                                            </div>
                                            <div style={{
                                                display: 'flex', 
                                                gap: '8px', 
                                                marginTop: '15px', 
                                                borderTop: '1px solid #eee', 
                                                paddingTop: '15px'
                                            }}>
                                                <button
                                                    onClick={() => handleEditCourse(course.id)}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px',
                                                        backgroundColor: '#e0effe',
                                                        color: '#1a56db',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontWeight: '600',
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    ✏️ Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCourse(course.id)}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px',
                                                        backgroundColor: '#fee2e2',
                                                        color: '#991b1b',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontWeight: '600',
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============= STYLES =============
const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
        backgroundColor: '#fff',
        borderBottom: '1px solid #e2e8f0',
        padding: '12px 0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    headerContent: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    headerTitle: {
        margin: 0,
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b'
    },
    headerSubtitle: {
        margin: '4px 0 0 0',
        fontSize: '14px',
        color: '#64748b'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    homeButton: {
        padding: '10px 20px',
        backgroundColor: '#f1f5f9',
        color: '#334155',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    logoutButton: {
        padding: '10px 20px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    main: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px'
    },
    tabContainer: {
        display: 'flex',
        gap: '12px',
        marginBottom: '32px',
        borderBottom: '2px solid #e2e8f0'
    },
    tab: {
        padding: '12px 24px',
        backgroundColor: 'transparent',
        color: '#64748b',
        border: 'none',
        borderBottom: '3px solid transparent',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    tabActive: {
        color: '#4f46e5',
        borderBottomColor: '#4f46e5'
    },
    content: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0'
    },
    progressContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '40px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px'
    },
    stepItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px'
    },
    stepActive: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#4f46e5',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '700',
        fontSize: '16px'
    },
    stepInactive: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#e2e8f0',
        color: '#94a3b8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '700',
        fontSize: '16px'
    },
    stepLabel: {
        fontSize: '13px',
        color: '#64748b',
        fontWeight: '600'
    },
    stepLine: {
        width: '80px',
        height: '2px',
        backgroundColor: '#e2e8f0',
        margin: '0 16px'
    },
    form: {
        width: '100%'
    },
    stepContent: {
        animation: 'fadeIn 0.3s ease'
    },
    stepTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '32px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
    },
    formGroup: {
        marginBottom: '24px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#334155'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.2s',
        fontFamily: 'inherit',
        resize: 'vertical',
        boxSizing: 'border-box'
    },
    textareaSmall: {
        width: '100%',
        padding: '10px 14px',
        fontSize: '14px',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.2s',
        fontFamily: 'inherit',
        resize: 'vertical',
        boxSizing: 'border-box'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        outline: 'none',
        backgroundColor: '#fff',
        cursor: 'pointer',
        boxSizing: 'border-box'
    },
    hint: {
        display: 'block',
        marginTop: '6px',
        fontSize: '13px',
        color: '#64748b'
    },
    gridTwo: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px'
    },
    imagePreview: {
        marginTop: '12px',
        width: '100%',
        maxHeight: '300px',
        objectFit: 'cover',
        borderRadius: '8px',
        border: '2px solid #e2e8f0'
    },
    objectiveItem: {
        display: 'flex',
        gap: '12px',
        marginBottom: '12px',
        alignItems: 'center'
    },
    inputWithButton: {
        flex: 1,
        padding: '12px 16px',
        fontSize: '15px',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    removeButton: {
        padding: '10px 16px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    addButton: {
        padding: '10px 20px',
        backgroundColor: '#eff6ff',
        color: '#2563eb',
        border: '2px dashed #93c5fd',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px',
        minHeight: '40px',
        padding: '8px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px'
    },
    tag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: '#4f46e5',
        color: '#fff',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '600'
    },
    tagClose: {
        backgroundColor: 'transparent',
        border: 'none',
        color: '#fff',
        fontSize: '14px',
        cursor: 'pointer',
        padding: '0',
        marginLeft: '4px'
    },
    tagInputGroup: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
    },
    tagAddButton: {
        padding: '12px 24px',
        backgroundColor: '#4f46e5',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
    },
    moduleCard: {
        backgroundColor: '#f8fafc',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px'
    },
    moduleHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
    },
    moduleTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b'
    },
    deleteButton: {
        padding: '8px 16px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    lessonsContainer: {
        marginTop: '20px'
    },
    lessonsTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#475569',
        marginBottom: '16px'
    },
    lessonCard: {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '12px'
    },
    lessonHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    lessonNumber: {
        fontSize: '13px',
        fontWeight: '700',
        color: '#4f46e5'
    },
    removeButtonSmall: {
        padding: '4px 12px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    addLessonButton: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#fff',
        color: '#4f46e5',
        border: '2px dashed #cbd5e1',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        marginTop: '12px'
    },
    addModuleButton: {
        width: '100%',
        padding: '14px',
        backgroundColor: '#eff6ff',
        color: '#2563eb',
        border: '2px dashed #93c5fd',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        marginTop: '24px'
    },
    buttonGroup: {
        display: 'flex',
        gap: '16px',
        justifyContent: 'flex-end',
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '2px solid #e2e8f0'
    },
    backButton: {
        padding: '12px 32px',
        backgroundColor: '#f1f5f9',
        color: '#334155',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    nextButton: {
        padding: '12px 32px',
        backgroundColor: '#4f46e5',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    publishButton: {
        padding: '14px 40px',
        backgroundColor: '#10b981',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
    },
    manageHeader: {
        marginBottom: '32px'
    },
    manageTitle: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '0 0 8px 0'
    },
    manageSubtitle: {
        fontSize: '14px',
        color: '#64748b',
        margin: 0
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px'
    },
    emptyIcon: {
        fontSize: '64px',
        marginBottom: '16px'
    },
    emptyTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '8px'
    },
    emptyText: {
        fontSize: '16px',
        color: '#64748b',
        marginBottom: '24px'
    },
    createFirstButton: {
        padding: '14px 32px',
        backgroundColor: '#4f46e5',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer'
    },
    coursesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '24px'
    },
    courseCard: {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'all 0.3s',
        cursor: 'pointer'
    },
    courseImage: {
        width: '100%',
        height: '180px',
        objectFit: 'cover'
    },
    courseContent: {
        padding: '20px'
    },
    courseBadge: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: '#eff6ff',
        color: '#2563eb',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '700',
        marginBottom: '12px'
    },
    courseTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '0 0 12px 0'
    },
    courseDescription: {
        fontSize: '14px',
        color: '#64748b',
        lineHeight: '1.6',
        marginBottom: '16px'
    },
    courseFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '16px',
        borderTop: '1px solid #e2e8f0'
    },
    coursePrice: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#10b981'
    },
    courseLevel: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        padding: '4px 12px',
        backgroundColor: '#f1f5f9',
        borderRadius: '6px'
    }
};

export default InstructorDashboard;